import * as React from "react";
import { loadGame } from "../lib/game-registry.js";

/**
 * Prefetch cache — prevents repeat calls for the same slug.
 * Keyed by slug, value is the in-flight or completed promise.
 */
const prefetchCache = new Map<string, Promise<unknown>>();

/**
 * Eagerly prefetch a game's JS chunk on hover/focus so that clicking
 * "Play" mounts the game nearly instantly. Only triggers the dynamic
 * import() — runtime assets (sprites, audio) are loaded after mount
 * by the game itself.
 *
 * Returns `{ onMouseEnter, onFocus }` props to spread onto the
 * interactive element (card, link, button).
 */
export function usePrefetchGame(slug: string | null | undefined): {
  onMouseEnter: () => void;
  onFocus: () => void;
} {
  const prefetch = React.useCallback(() => {
    if (!slug) return;
    if (prefetchCache.has(slug)) return;

    const loader = loadGame(slug);
    if (!loader) return;

    prefetchCache.set(slug, loader.catch(() => {
      prefetchCache.delete(slug);
    }));
  }, [slug]);

  return { onMouseEnter: prefetch, onFocus: prefetch };
}
